import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { getDeepResearchResult, startDeepResearch, getProjectWithRfi } from "@/lib/api/projects";
import { BackendError } from "@/lib/backend";
import { ProjectHeader } from "@/components/ProjectHeader";
import { useProjectRole, NO_PERMISSION_TOOLTIP } from "@/hooks/useProjectRole";
import type { DeepResearchResultResponse } from "@/types/backend";

type DeepResearchSource = NonNullable<DeepResearchResultResponse["processed_sources"]>[number];

function normalizeCiteUrlForMarkdown(url: string) {
  // Minimal defensive encoding so CommonMark link destination parsing is stable.
  // Important: keep this normalization consistent between markdown generation and href comparison.
  return url.replace(/</g, "%3C").replace(/>/g, "%3E");
}

function getSingleTextChild(children: unknown): string | null {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children) && children.length === 1) {
    const only = children[0];
    if (typeof only === "string") return only;
    if (typeof only === "number") return String(only);
  }
  return null;
}

function buildSourcesById(sources: DeepResearchSource[] | undefined | null) {
  const map = new Map<number, DeepResearchSource>();
  if (!Array.isArray(sources)) return map;
  for (const s of sources) {
    if (typeof s?.id === "number") map.set(s.id, s);
  }
  return map;
}

function withCiteLinks(
  markdown: string,
  sourcesById: Map<number, DeepResearchSource>,
) {
  // Turns "{{cite:1,2}}" into "[1](url) [2](url)" so we can style links as "bubbles".
  return markdown.replace(/\{\{cite:([^}]+)\}\}/g, (_m, raw: string) => {
    const ids = raw
      .split(",")
      .map((x) => Number.parseInt(x.trim(), 10))
      .filter((n) => Number.isFinite(n));

    const parts = ids
      .map((id) => {
        const s = sourcesById.get(id);
        if (!s?.url) return null;
        const title = (s.label || s.domain || `Source ${id}`).replace(/"/g, `'`);
        // Wrap destination in angle brackets to avoid markdown parse issues with special chars (e.g. ')' in URLs).
        // See: https://spec.commonmark.org/0.30/#link-destination
        const safeUrl = normalizeCiteUrlForMarkdown(s.url);
        return `[${id}](<${safeUrl}> "${title}")`;
      })
      .filter(Boolean) as string[];

    return parts.length ? ` ${parts.join(" ")}` : "";
  });
}

export default function DeepResearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId")?.trim() || null;

  // Fetch project data for header
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectId ? getProjectWithRfi(projectId) : Promise.reject('No project ID'),
    enabled: !!projectId,
  });

  const handleBackToProject = () => {
    if (projectId) {
      navigate(`/project/${projectId}`);
    } else {
      navigate('/');
    }
  };

  const { canEdit } = useProjectRole(projectId);

  const [data, setData] = useState<DeepResearchResultResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notStarted, setNotStarted] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getDeepResearchResult(projectId);
      setData(result);
      setNotStarted(false);
    } catch (e) {
      if (e instanceof BackendError && e.status === 404) {
        // Deep Research was not started yet for this project.
        setData(null);
        setNotStarted(true);
        setError(null);
        return;
      }

      const message =
        e instanceof BackendError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : "Unknown error";
      setError(message);
      toast({
        title: "Failed to load Deep Research",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const handleGenerate = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      await startDeepResearch(projectId);
      setNotStarted(false);
      await load();
    } catch (e) {
      const message =
        e instanceof BackendError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : "Unknown error";
      setError(message);
      toast({
        title: "Failed to start Deep Research",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [load, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sourcesById = useMemo(
    () => buildSourcesById(data?.processed_sources),
    [data?.processed_sources],
  );

  return (
    <div className="min-h-screen bg-background">
      <ProjectHeader title={projectData?.name} />
      <main className="container mx-auto px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground tracking-tight">
                Deep Research
              </h1>
              {!projectId && (
                <p className="text-sm text-muted-foreground mt-1">
                  Open this page from a project to provide <code>projectId</code>.
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => void handleGenerate()}
                disabled={!projectId || loading || !notStarted || !canEdit}
                title={!canEdit ? NO_PERMISSION_TOOLTIP : undefined}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Generate
              </Button>

            </div>
          </div>

          {!projectId && (
            <Card className="border-border/50">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                Please open Deep Research from a project page.
              </CardContent>
            </Card>
          )}

          {projectId && !data && loading && (
            <Card className="border-border/50">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                <Loader2 className="w-5 h-5 mx-auto animate-spin mb-3 text-foreground/40" />
                Loading Deep Research…
              </CardContent>
            </Card>
          )}

          {projectId && notStarted && !loading && !error && (
            <Card className="border-border/50">
              <CardContent className="py-10 text-center">
                <p className="text-sm font-medium text-foreground">
                  Deep Research has not been generated yet
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click Generate to start it. It may take up to 30 minutes.
                </p>
              </CardContent>
            </Card>
          )}

          {projectId && error && (
            <Card className="border-border/50">
              <CardContent className="py-8">
                <p className="text-sm font-medium text-foreground mb-1">
                  Failed to load
                </p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </CardContent>
            </Card>
          )}

          {projectId && data && data.status === "pending" && (
            <Card className="border-border/50">
              <CardContent className="py-10 text-center">
                <p className="text-sm font-medium text-foreground">
                  Deep Research is still running
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click Refresh to check the latest status.
                </p>
              </CardContent>
            </Card>
          )}

          {projectId && data && data.status === "failed" && (
            <Card className="border-border/50">
              <CardContent className="py-10 text-center">
                <p className="text-sm font-medium text-foreground">
                  Deep Research failed
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click Refresh to retry fetching the result.
                </p>
              </CardContent>
            </Card>
          )}

          {projectId && data && data.status === "completed" && (
            <div className="space-y-6">
              <Card className="border-border/50">
                <CardContent className="py-8">
                  {data.processed_markdown ? (
                    <div className="prose prose-neutral dark:prose-invert max-w-none whitespace-pre-line">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ href, children }) => {
                            const label = getSingleTextChild(children);
                            const id =
                              label && /^\d+$/.test(label)
                                ? Number.parseInt(label, 10)
                                : null;
                            const isCite = Boolean(
                              id &&
                                href &&
                                normalizeCiteUrlForMarkdown(sourcesById.get(id)?.url ?? "") === href,
                            );
                            return (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={
                                  isCite
                                    ? "inline-flex items-center rounded-full border border-border/60 bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/80 no-underline hover:bg-muted/70"
                                    : undefined
                                }
                                title={
                                  isCite
                                    ? sourcesById.get(id!)?.label || sourcesById.get(id!)?.domain
                                    : undefined
                                }
                              >
                                {children}
                              </a>
                            );
                          },
                          table: ({ children }) => (
                            <div className="not-prose my-5 overflow-x-auto rounded-md border border-border/60">
                              <table className="w-full border-collapse text-sm">{children}</table>
                            </div>
                          ),
                          thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
                          tr: ({ children }) => <tr className="bg-background">{children}</tr>,
                          th: ({ children }) => (
                            <th className="border border-border/60 px-3 py-2 text-left font-semibold">
                              {children}
                            </th>
                          ),
                          td: ({ children }) => (
                            <td className="border border-border/60 px-3 py-2 align-top text-foreground/90">
                              {children}
                            </td>
                          ),
                        }}
                      >
                        {withCiteLinks(data.processed_markdown, sourcesById)}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Deep Research completed, but no processed markdown was returned.
                    </p>
                  )}
                </CardContent>
              </Card>

              {Array.isArray(data.processed_sources) &&
                data.processed_sources.length > 0 && (
                  <Card className="border-border/50">
                    <CardContent className="py-6">
                      <h2 className="text-sm font-semibold text-foreground mb-3">
                        Sources
                      </h2>
                      <ul className="space-y-2 text-sm">
                        {data.processed_sources.map((s) => (
                          <li key={s.id} className="flex items-start gap-2">
                            <span className="text-muted-foreground w-5 flex-shrink-0">
                              {s.id}.
                            </span>
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {s.label}
                              <span className="text-muted-foreground/60 ml-1">
                                — {s.domain}
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
